import { Fragment, useMemo, type ReactNode } from "react";

import createCache, { type EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import dynamic from "next/dynamic";

import {
  ChakraProvider as ChakraUIProvider,
  type SystemContext
} from "@chakra-ui/react";

import {
  createSystemFromTheme,
  defaultSystem,
  extendTheme,
  theme as baseTheme
} from "../theme/theme";

const AppToaster = dynamic(
  () => import("../toast/toaster").then((mod) => mod.AppToaster),
  { ssr: false }
);

function resolveSystem(
  value?: SystemContext,
  themeOverrides?: Record<string, unknown>
): SystemContext {
  if (value) {
    return value;
  }

  try {
    const merged = themeOverrides
      ? extendTheme(baseTheme, themeOverrides)
      : baseTheme;
    return createSystemFromTheme(merged);
  } catch {
    return defaultSystem;
  }
}

export interface ProviderProps {
  children: ReactNode;
  value?: SystemContext;
  /** v2-compat: runtime theme overrides, converted via `createSystemFromTheme`. */
  theme?: Record<string, unknown>;
  themeOverrides?: Record<string, unknown>;
  nonce?: string;
  /**
   * Reuse an existing Emotion cache. The App Router passes the registry
   * cache so client navigations do not drop styles.
   */
  emotionCache?: EmotionCache;
}

export function ChakraRoot({
  children,
  value,
  theme,
  themeOverrides
}: Omit<ProviderProps, "nonce" | "emotionCache">) {
  const overrides = theme ?? themeOverrides;
  const system = useMemo(
    () => (value ? value : resolveSystem(undefined, overrides)),
    [value, overrides]
  );

  return (
    <ChakraUIProvider value={system}>
      <Fragment>
        {children}
        <AppToaster />
      </Fragment>
    </ChakraUIProvider>
  );
}

export function Provider({
  children,
  nonce = "",
  emotionCache,
  ...chakraProps
}: ProviderProps) {
  const ownedCache = useMemo(
    () =>
      emotionCache ?? createCache({ key: "ehq-contentsecuritypolicy", nonce }),
    [emotionCache, nonce]
  );

  return (
    <CacheProvider value={ownedCache}>
      <ChakraRoot {...chakraProps}>{children}</ChakraRoot>
    </CacheProvider>
  );
}

/** @deprecated Prefer `Provider`. */
export const AppProviders = Provider;

export type AppProvidersProps = ProviderProps;

/** @deprecated Prefer `Provider` / `ChakraRoot`. */
export function ChakraProvider(
  props: Omit<ProviderProps, "nonce" | "emotionCache">
) {
  return <ChakraRoot {...props} />;
}

export type ChakraProviderProps = Omit<ProviderProps, "nonce" | "emotionCache">;

/** @deprecated Prefer `Provider` with `themeOverrides`. */
export function UiProvider({
  nonce,
  ehqTheme,
  children
}: {
  nonce?: string;
  ehqTheme?: Record<string, unknown>;
  children: ReactNode;
}) {
  return (
    <Provider nonce={nonce} themeOverrides={ehqTheme}>
      {children}
    </Provider>
  );
}
